/**
 * Watchdog · Notification-Center — Übersicht (FR-5)
 *
 * Der Freigabe-Ort für Tracking-Alarme. Check and Score schreibt je
 * fehlgeschlagenem Scoring eine Zeile; das DigA-Team entscheidet hier, ob der
 * Alarm valide ist. Nur `confirmed` wird später für ambix sichtbar.
 *
 * Es gibt bewusst KEIN Löschen — nur Status-Wechsel.
 *
 * Default-Filter ist `new`; Gelöste lassen sich per Toggle einblenden.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell, Chip,
  CircularProgress, Alert, Tooltip, IconButton, Stack, Switch, FormControlLabel, Button,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorageIcon from '@mui/icons-material/Storage';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import ClearIcon from '@mui/icons-material/Clear';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { colors } from '../../styles/theme.js';
import { api } from '../../api/client.js';
import type {
  NotificationListItem, NotificationListResponse, NotificationStatus, NotificationSeverity,
} from '../../../shared/watchdogTypes.js';

/**
 * Die severity-Skala bewertet Scorings. Sie ist bewusst eine ANDERE Farbwelt als
 * der job_status im Job-Manager — dort geht es um Läufe, hier um Befunde.
 */
export const SEVERITY_META: Record<NotificationSeverity, { label: string; color: string; text: string }> = {
  notice: { label: 'Hinweis', color: colors.scoreYellow, text: colors.black },
  warning: { label: 'Warnung', color: '#f97316', text: colors.white },
  showstopper: { label: 'Showstopper', color: colors.scoreRed, text: colors.white },
};

export const STATUS_META: Record<NotificationStatus, { label: string; color: string }> = {
  new: { label: 'offen', color: '#3b82f6' },
  confirmed: { label: 'bestätigt', color: colors.scoreRed },
  false_alarm: { label: 'Fehlalarm', color: colors.black60 },
};

/** Showstopper zuerst — die Sortierung der Liste bleibt aber zeitlich. */
const SEVERITY_RANK: Record<NotificationSeverity, number> = {
  showstopper: 0, warning: 1, notice: 2,
};

export function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

export default function NotificationCenterPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [items, setItems] = useState<NotificationListItem[]>([]);
  const [counts, setCounts] = useState<Record<NotificationStatus, number>>({
    new: 0, confirmed: 0, false_alarm: 0,
  });
  const [dbProfile, setDbProfile] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Der Slack-Deep-Link kann Domain und Lauf vorfiltern
  // (notificationRunLink → ?domain=…&run=…&status=new).
  const domainFilter = searchParams.get('domain');
  const runFilter = searchParams.get('run');
  // Default ist „nur offene". Der Toggle schaltet auf „alle".
  const showResolved = searchParams.get('status') === 'all';

  const load = useCallback(async () => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (!showResolved) qs.set('status', 'new');
    if (domainFilter) qs.set('domain', domainFilter);
    if (runFilter) qs.set('run', runFilter);
    const query = qs.toString();

    const res = await api.get<NotificationListResponse>(
      `/watchdog/notifications${query ? `?${query}` : ''}`,
    );
    if (res.success) {
      setItems(res.data.notifications);
      setCounts(res.data.counts);
      setDbProfile(res.data.db_profile);
      setError(null);
    } else {
      setError(res.error.message);
    }
    setLoading(false);
  }, [showResolved, domainFilter, runFilter]);

  useEffect(() => { void load(); }, [load]);

  const toggleResolved = (checked: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (checked) next.set('status', 'all'); else next.delete('status');
    setSearchParams(next, { replace: true });
  };

  const clearScopeFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('domain');
    next.delete('run');
    setSearchParams(next, { replace: true });
  };

  // Neueste zuerst; bei gleichem Zeitstempel entscheidet die Schwere.
  const sorted = [...items].sort((a, b) => {
    const t = new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    return t !== 0 ? t : SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
  });

  return (
    <Box sx={{ p: 3, maxWidth: 1600, mx: 'auto' }}>
      {/* ── Kopf ─────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Notification-Center</Typography>
          <Typography variant="body2" color="text.secondary">
            Tracking-Alarme prüfen und freigeben. Checks mit „DigA-Freigabe notwendig" (Einstellungen,
            Watchdog) warten hier, bevor ambix den Befund zeigt. Checks ohne diesen Schalter sind in
            ambix bereits sichtbar.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {dbProfile && (
            <Tooltip title="Datenbank-Umgebung dieser Ansicht — Wechsel über die Menüleiste">
              <Chip
                icon={<StorageIcon sx={{ fontSize: 16 }} />}
                label={dbProfile}
                size="small"
                sx={{ fontWeight: 600, bgcolor: colors.lightGrey, border: `1px solid ${colors.silver}` }}
              />
            </Tooltip>
          )}
          <Tooltip title="Neu laden">
            <span>
              <IconButton onClick={() => void load()} disabled={loading} size="small">
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {/* ── Zaehler und Filter ───────────────────────────── */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }} useFlexGap>
        <Chip
          label={`offen ${counts.new}`}
          sx={{ fontWeight: 600, bgcolor: STATUS_META.new.color, color: colors.white }}
        />
        <Chip label={`bestätigt ${counts.confirmed}`} variant="outlined" sx={{ fontWeight: 600 }} />
        <Chip label={`Fehlalarm ${counts.false_alarm}`} variant="outlined" sx={{ fontWeight: 600 }} />

        <Box sx={{ flex: 1 }} />

        <FormControlLabel
          control={<Switch checked={showResolved} onChange={(e) => toggleResolved(e.target.checked)} />}
          label={<Typography variant="body2">Gelöste einblenden</Typography>}
        />
      </Stack>

      {(domainFilter || runFilter) && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={<Button size="small" startIcon={<ClearIcon />} onClick={clearScopeFilter}>Filter aufheben</Button>}
        >
          Eingeschränkt auf
          {runFilter && <> Lauf <code>{runFilter}</code></>}
          {runFilter && domainFilter && ' ·'}
          {domainFilter && <> Domain <code>{domainFilter}</code></>}
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* ── Tabelle ──────────────────────────────────────── */}
      <Paper elevation={0} sx={{ border: `1px solid ${colors.silver}`, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
        ) : sorted.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <NotificationsNoneIcon sx={{ fontSize: 40, color: colors.silver, mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {showResolved
                ? 'Keine Notifications vorhanden.'
                : 'Keine offenen Alarme — alles geprüft.'}
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700, width: 6, p: 0 }} />
                <TableCell sx={{ fontWeight: 700 }}>Datum</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Domain</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Scoring</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Schwere</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Entschieden</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((n) => {
                const sev = SEVERITY_META[n.severity];
                const st = STATUS_META[n.notification_status];
                return (
                  <TableRow
                    key={n.id}
                    hover
                    onClick={() => navigate(`/watchdog/notifications/${n.id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    {/* Farbstreifen statt eingefaerbter Zeile — bleibt lesbar */}
                    <TableCell sx={{ p: 0, bgcolor: sev.color, width: 6 }} />

                    <TableCell>
                      <Typography variant="caption">{formatDate(n.created_at)}</Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {n.hostname ?? <em>unbekannt</em>}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ maxWidth: 560 }}>
                      {/* Klartext-Frage aus twd_scoring_definitions.question */}
                      <Typography variant="body2">
                        {n.question ?? <em>Definition „{n.scoring_code}" nicht mehr vorhanden</em>}
                      </Typography>
                      {/* Seit 12.10.2026 je Ziel eine Meldung — der Kontext sagt, welches */}
                      {n.target_context && (
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {n.target_context}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {n.scoring_code}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={sev.label}
                        size="small"
                        sx={{ bgcolor: sev.color, color: sev.text, fontWeight: 600, fontSize: '0.7rem' }}
                      />
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={st.label}
                        size="small"
                        variant={n.notification_status === 'new' ? 'filled' : 'outlined'}
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          ...(n.notification_status === 'new'
                            ? { bgcolor: st.color, color: colors.white }
                            : { borderColor: st.color, color: st.color }),
                        }}
                      />
                      {n.resolved_at && (
                        <Tooltip title="Derselbe Check hat für dasselbe Ziel danach wieder bestanden — reißt er erneut, entsteht eine neue Meldung.">
                          <Chip
                            label={`wieder grün ${formatDate(n.resolved_at)}`}
                            size="small"
                            variant="outlined"
                            sx={{ ml: 0.5, fontWeight: 600, fontSize: '0.7rem', borderColor: colors.scoreGreen, color: colors.scoreGreen }}
                          />
                        </Tooltip>
                      )}
                    </TableCell>

                    <TableCell>
                      {n.decided_by ? (
                        <>
                          <Typography variant="caption" sx={{ display: 'block' }}>{n.decided_by}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(n.decided_at)}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
}
